import { sql } from "drizzle-orm";
import {
  check,
  pgTable,
  varchar,
  uuid,
  jsonb,
  bigint,
  timestamp,
  integer,
  date,
  text,
  uniqueIndex,
  index,
  primaryKey,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";

export const retentionRecords = pgTable(
  "crm_x_retention_records",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    retentionClass: varchar("retention_class", { length: 64 }).notNull(),
    recordType: varchar("record_type", { length: 128 }).notNull(),
    payload: jsonb("payload").notNull().default({}),
    payloadPiiSplit: jsonb("payload_pii_split"),
    sourceSystem: varchar("source_system", { length: 128 }).notNull(),
    sourceRecordId: text("source_record_id").notNull(),
    organisationId: uuid("organisation_id").notNull(),
    rowSeq: bigint("row_seq", { mode: "number" }).notNull(),
    prevHash: varchar("prev_hash", { length: 64 }).notNull(),
    rowHash: varchar("row_hash", { length: 64 }).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    anchorStatus: varchar("anchor_status", { length: 32 }).notNull().default("pending-anchor"),
    anchorId: uuid("anchor_id").references(() => retentionAnchorReceipts.id),
  },
  (table) => ({
    orgSeqIdx: index("idx_retention_records_org_seq").on(table.organisationId, table.rowSeq),
    recordTypeSourceIdx: index("idx_retention_records_record_type_source").on(
      table.recordType,
      table.sourceSystem,
      table.sourceRecordId,
    ),
    anchorStatusIdx: index("idx_retention_records_anchor_status").on(
      table.anchorStatus,
      table.rowSeq,
    ),
  }),
);

export const retentionAnchorReceipts = pgTable(
  "crm_x_retention_anchor_receipts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    anchorDate: date("anchor_date").notNull(),
    chainHeadHash: varchar("chain_head_hash", { length: 64 }).notNull(),
    rowCount: integer("row_count").notNull(),
    firstSeq: bigint("first_seq", { mode: "number" }),
    lastSeq: bigint("last_seq", { mode: "number" }),
    r2ObjectKey: text("r2_object_key").notNull(),
    r2Etag: text("r2_etag").notNull(),
    objectLockRetainUntil: timestamp("object_lock_retain_until", {
      withTimezone: true,
    }).notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    anchorDateKeyCheck: check(
      "retention_anchor_receipts_date_key_check",
      sql`(${table.anchorDate} IS NOT NULL
        AND ${table.r2ObjectKey} IS NOT NULL
        AND pg_catalog.isfinite(${table.anchorDate})
        AND ${table.anchorDate} BETWEEN DATE '0001-01-01' AND DATE '9999-12-31'
        AND ${table.r2ObjectKey} = 'retention-anchors/' || pg_catalog.to_char(${table.anchorDate}::timestamp without time zone, 'YYYY-MM-DD') || '.json') IS TRUE`,
    ),
    anchorDateUnique: uniqueIndex("crm_x_retention_anchor_receipts_anchor_date_key").on(
      table.anchorDate,
    ),
    anchorDateIdx: index("idx_retention_anchor_receipts_date").on(table.anchorDate),
  }),
);

export const retentionPiiAllowlist = pgTable(
  "crm_x_retention_pii_allowlist",
  {
    recordType: varchar("record_type", { length: 128 }).notNull(),
    payloadKey: text("payload_key").notNull(),
    patternClass: varchar("pattern_class", { length: 32 }).notNull(),
    rationale: text("rationale").notNull(),
    addedBy: text("added_by").notNull(),
    addedAt: timestamp("added_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (table) => ({
    primaryKey: primaryKey({
      columns: [table.recordType, table.payloadKey, table.patternClass],
    }),
    patternClassCheck: check(
      "crm_x_retention_pii_allowlist_pattern_class_check",
      sql`${table.patternClass} IN ('TFN', 'ABN', 'Medicare', 'BSB_ACCOUNT', 'EMAIL', 'PHONE', 'DOB', 'ADDRESS', 'PERSON_NAME')`,
    ),
    rationaleNotBlank: check(
      "crm_x_retention_pii_allowlist_rationale_check",
      sql`btrim(${table.rationale}) <> ''`,
    ),
    addedByNotBlank: check(
      "crm_x_retention_pii_allowlist_added_by_check",
      sql`btrim(${table.addedBy}) <> ''`,
    ),
  }),
);

export const insertRetentionRecordSchema = createInsertSchema(retentionRecords);
export const selectRetentionRecordSchema = createSelectSchema(retentionRecords);
export const insertRetentionAnchorReceiptSchema = createInsertSchema(retentionAnchorReceipts);
export const selectRetentionAnchorReceiptSchema = createSelectSchema(retentionAnchorReceipts);
export const insertRetentionPiiAllowlistSchema = createInsertSchema(retentionPiiAllowlist);
export const selectRetentionPiiAllowlistSchema = createSelectSchema(retentionPiiAllowlist);

export type RetentionRecord = typeof retentionRecords.$inferSelect;
export type InsertRetentionRecord = typeof retentionRecords.$inferInsert;
export type RetentionAnchorReceipt = typeof retentionAnchorReceipts.$inferSelect;
export type InsertRetentionAnchorReceipt = typeof retentionAnchorReceipts.$inferInsert;
export type RetentionPiiAllowlist = typeof retentionPiiAllowlist.$inferSelect;
export type InsertRetentionPiiAllowlist = typeof retentionPiiAllowlist.$inferInsert;
